import { type FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, FormContext, Flex, type SelectProps, TooltipWrapper, Box } from 'dash-ui';

import { FormFields } from 'containers/properties/PropertyGeneralInformation/components/PropertyGeneralInfoForm/constants/constants';
import useValidation from 'containers/properties/PropertyGeneralInformation/components/PropertyGeneralInfoForm/hooks/useValidation';
import { useGetOrganizationsForSelectQuery } from 'gql/generated.graphql';

const OrganizationSelect: FC<SelectProps> = ({ ...selectProps }) => {
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const { formErrors, formState, stateHandlers } = useContext(FormContext);
  const valid = useValidation();
  const { data, loading } = useGetOrganizationsForSelectQuery({
    variables: { pagination: { allowLimit: false } },
  });
  const organizationsOptions = useMemo(() => {
    const organizations = data?.getOrganizations?.collection || [];

    return (
      organizations?.map((item) => ({
        value: item._id,
        label: item.name,
      })) || []
    );
  }, [data]);

  const handleSelectInputs =
    (fieldName: FormFields) => (value: string, values?: string[]) => {
      stateHandlers[fieldName](values || value);
      valid(fieldName, values || value);
    };

  const tooltipValue = useMemo(() => {
    return organizationsOptions
      .filter((org) => (formState[FormFields.Organizations] || []).includes(org.value))
      .map((sv) => sv?.label)
      .join(', ');
  }, [formState, organizationsOptions]);

  return (
    <Flex flexDirection="column" mr={2} flex={1}>
      <Form.Label>{t('forms:labels.additionalOrganizations')}</Form.Label>
      <TooltipWrapper w="100%" content={tooltipValue}>
        <Form.Select
          isMulti
          menuPortalZIndex="auto"
          isLoading={loading}
          value={
            formState[FormFields.Organizations]?.length
              ? formState[FormFields.Organizations]
              : undefined
          }
          options={organizationsOptions.filter(
            (item) => item.value !== formState[FormFields.PrimaryOrganization]
          )}
          placeholder={t('forms:placeholders.selectOne')}
          w="100%"
          onChanged={handleSelectInputs(FormFields.Organizations)}
          $errorType={formErrors[FormFields.Organizations]?.type}
          {...selectProps}
        />
      </TooltipWrapper>
      <Form.Feedback $errorType={formErrors[FormFields.Organizations]?.type}>
        {formErrors[FormFields.Organizations]?.message}
      </Form.Feedback>
    </Flex>
  );
};

export default OrganizationSelect;
